import React from 'react';
import { connect } from 'react-redux';
import {
  colors,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import { darken, lighten } from '@mui/material/styles';
import { CacheProvider, EmotionCache } from '@emotion/react';
import createCache from '@emotion/cache';
import tinycolor from 'tinycolor2';

let emotionCache: EmotionCache;

/**
 * Keeps the v4 (JSS) cascade order: CSS modules, then Material UI styles, then our withStyles/makeStyles.
 * The insertion point is appended to <head> on first render, after the main bundle's CSS, so Material UI
 * styles still outrank CSS modules as they did in v4. JSS sheets are appended after it. An existing insertion
 * point is reused so a hot reload of this module does not move Material UI styles after the JSS sheets.
 */
const getEmotionCache = (): EmotionCache => {
  if (!emotionCache) {
    let insertionPoint = document.head.querySelector<HTMLElement>('meta[name="emotion-insertion-point"]');
    if (!insertionPoint) {
      insertionPoint = document.createElement('meta');
      insertionPoint.setAttribute('name', 'emotion-insertion-point');
      document.head.appendChild(insertionPoint);
    }
    emotionCache = createCache({ key: 'css', insertionPoint });
  }
  return emotionCache;
};

export class ThemeWrapperComponent extends React.Component<any, any> {
  render() {
    const {
      primaryColor   = '#E0E0E0',
      secondaryColor = '#E0E0E0',
      tertiaryColor  = '#E0E0E0',
    } = this.props;

    const contrastPrimaryColor = tinycolor(primaryColor).isLight() ? colors.common.black : colors.common.white;
    const contrastSecondaryColor = tinycolor(secondaryColor).isLight() ? colors.common.black : colors.common.white;

    let theme = createTheme({
      // v5 changed the default breakpoint widths (md 960 -> 900, lg 1280 -> 1200, xl 1920 -> 1536). The app is built
      // on the v4 widths, including CSS modules with hard-coded 959.95px / 599.95px media queries, so keep them.
      breakpoints: {
        values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
      },
      palette: {
        // v5 changed the default page background from grey[50] to white.
        background: {
          default: colors.grey[50],
        },
        // v5 darkened secondary text from 54% to 60% black; keep the v4 value.
        text: {
          secondary: 'rgba(0, 0, 0, 0.54)',
        },
        // v5 darkened the default status colors; keep the v4 values.
        error: {
          light: colors.red[300],
          main : colors.red[500],
          dark : colors.red[700],
        },
        warning: {
          light: colors.orange[300],
          main : colors.orange[500],
          dark : colors.orange[700],
        },
        info: {
          light: colors.blue[300],
          main : colors.blue[500],
          dark : colors.blue[700],
        },
        success: {
          light: colors.green[300],
          main : colors.green[500],
          dark : colors.green[700],
        },
        primary: {
          main: `${primaryColor}`,
          contrastText: contrastPrimaryColor,
        },
        secondary: {
          main: `${secondaryColor}`,
          contrastText: contrastSecondaryColor,
        },
        tertiary: {
          main: `${tertiaryColor}`,
          dark: darken(`${tertiaryColor}`, 0.1),
          light: lighten(`${tertiaryColor}`, 0.15),
          contrastText: colors.common.white,
        },
      },
    });

    theme = {
      ...theme,
      components: {
        MuiTypography: {
          styleOverrides: {
            root: {
              color: colors.grey[800],
              '&.light': {
                fontWeight: 300,
                '&.MuiTypography-body2, &.MuiTypography-body1': {
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 15,
                  },
                },
              },
              '&.specialFont': {
                fontFamily: '"Roboto Slab",serif',
              },
              '&.semiBold': {
                fontWeight: 500,
              },
              '&.bold': {
                fontWeight: 700,
              },
              '&.white': {
                color: colors.common.white,
              },
              '&.mobileWhite': {
                [theme.breakpoints.down('md')]: {
                  color: colors.common.white,
                },
              },
              '&.mobileCenter': {
                [theme.breakpoints.down('md')]: {
                  textAlign: 'center',
                },
              },
              '&.mobileTextShadow': {
                [theme.breakpoints.down('md')]: {
                  textShadow: 'rgba(0,0,0,.5) 0 2px 9px',
                },
              },
              '&.heroTitle': {
                color: colors.common.white,
                fontWeight: 700,
                fontSize: 60,
                textShadow: '2px 2px 4px rgba(97,97,97,0.5)',
                letterSpacing: '.2px',
              },
              '&.branchTitle': {
                color: colors.common.white,
                fontSize: 50,
                textShadow: '0 2px 5px rgba(0,0,0,0.5)',
                textAlign: 'center',
                fontFamily: '"Roboto",sans-serif',
                display: 'block',
                [theme.breakpoints.down('lg')]: {
                  fontSize: 40,
                },
                [theme.breakpoints.down('sm')]: {
                  fontSize: 25,
                },
              },
              '&.tileBoxTitle': {
                fontSize: 18,
                lineHeight: '24px',
                fontWeight: 500,
              },
              '&.smallTitle': {
                fontSize  : 18,
                fontWeight: 300,
                margin    : '0 0 2px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: 16,
                  marginBottom: '5px',
                },
              },
              '&.loanDisplay': {
                lineHeight: '40px',
                fontSize: 18,
                [theme.breakpoints.down('sm')]: {
                  fontSize: 17,
                },
              },
              '&.infoMessage': {
                overflow: 'hidden',
                marginBottom: '32px',
                lineHeight: 1.4,
              },
              '&.mobileAsset': {
                [theme.breakpoints.down('sm')]: {
                  fontSize: 15,
                },
                '&.largeLabel': {
                  fontSize: 18,
                  fontWeight: 400,
                  marginLeft: '30px',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 15,
                    marginLeft: '16px',
                  },
                },
                '&.largeAsset': {
                  fontSize: 25,
                  fontWeight: 700,
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 15,
                  },
                  '&.warning': {
                    whiteSpace: 'nowrap',
                  },
                },
              },
              '&.modal': {
                '& h1, h2': {
                  fontFamily: '"Roboto Slab",serif',
                  marginTop: 0,
                },
              },
              '&.infoTitle': {
                fontWeight: 500,
                marginBottom: '8px',
              },
              '&.sectionData': {
                [theme.breakpoints.down('sm')]: {
                  fontSize: 14,
                },
                '&.sectionDataTitle': {
                  fontWeight: 500,
                  color: colors.grey[500],
                },
              },
              '&.subtitle': {
                paddingBottom: '20px',
                lineHeight: 1.4,
              },
              '&.clearWrapper': {
                fontSize: 30,
                [theme.breakpoints.up('md')]: {
                  marginTop: 0,
                },
                [theme.breakpoints.down('sm')]: {
                  fontSize: 18,
                },
                '&.scheduleTitle': {
                  marginTop: 0,
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 20,
                    marginBottom: '25px',
                  },
                },
              },
              '&.titleMargin': {
                marginTop: '20px',
                [theme.breakpoints.up('md')]: {
                  marginTop: '90px',
                },
                '&.section': {
                  [theme.breakpoints.down('md')]: {
                    marginTop: 0,
                  },
                },
              },
              '&.disclaimer': {
                fontSize: '12px',
                lineHeight: 1.4,
                textAlign: 'left',
                marginBottom: '15px',
                '&.rateTable': {
                  lineHeight: 1.5,
                  textAlign: 'center',
                  marginTop: '6px',
                  marginBottom: '1em',
                  fontSize: '12px',
                  padding: '0 26px',
                  [theme.breakpoints.down('md')]: {
                    margin: '12px 0',
                  },
                  [theme.breakpoints.down('sm')]: {
                    padding: '0 16px',
                  },
                },
                '&.center': {
                  textAlign: 'center',
                },
              },
              '&.paddingBottom': {
                [theme.breakpoints.up('sm')]: {
                  paddingBottom: '20px',
                },
              },
            },
            // In v4 these sat on `root` but MUI's built-in variant styles outranked them, so they only reached
            // variant="inherit". v5 styleOverrides outrank the built-in variants, so scope them to match v4.
            inherit: {
              letterSpacing: 0.2,
              fontFamily: '"Roboto",sans-serif',
              fontSize: 16,
            },
            h6: {
              fontFamily: '"Roboto Slab",serif',
              color: colors.grey[800],
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: 0,
              margin: '6px 0 16px',
              lineHeight: 1.3,
              display: 'inline-block',
              [theme.breakpoints.up('sm')]: {
                fontSize: 22,
                margin: '16px 0',
              },
              [theme.breakpoints.up('md')]: {
                marginTop: '22px',
                fontSize: 25,
              },
              '&.sectionFooterTitle': {
                display: 'block',
                [theme.breakpoints.up('md')]: {
                  textAlign: 'center',
                },
                [theme.breakpoints.down('md')]: {
                  margin: 0,
                },
              },
              '&.withTopSection': {
                marginTop: '20px',
              },
              '&.landingTitle': {
                marginBottom: '5px',
              },
              '&.contentTitle': {
                [theme.breakpoints.up('md')]: {
                  marginTop: '50px',
                },
              },
              '&.smallSummaryTitle': {
                marginBottom: '33px',
                textAlign: 'left',
                [theme.breakpoints.down('md')]: {
                  fontSize: 20,
                  margin: '0 0 10px',
                },
              },
              '&.splashTitle': {
                [theme.breakpoints.down('md')]: {
                  fontSize: 25,
                },
              },
              '&.centerTitle': {
                display: 'block',
                textAlign: 'center',
              },
              '&.noMarginTop': {
                marginTop: 0,
              },
              '&.noMarginBottom': {
                marginBottom: 0,
              },
            },
            h5: {
              color   : colors.grey[800],
              fontSize: 25,
            },
            subtitle1: {
              color: colors.grey[800],
              fontWeight: 700,
              '&.large': {
                fontSize: 20,
                [theme.breakpoints.down('md')]: {
                  fontSize: 18,
                },
                [theme.breakpoints.down('sm')]: {
                  fontSize: 16,
                },
              },
              '&.branchSubheading': {
                fontSize: 30,
                fontWeight: 500,
                color: colors.grey[900],
                marginTop: '30px',
                lineHeight: 1.25,
                [theme.breakpoints.down('md')]: {
                  fontSize: 25,
                  padding: '0 30px 10px',
                },
                [theme.breakpoints.down('sm')]: {
                  fontSize: 20,
                  padding: 0,
                },
              },
            },
            h1: {
              fontFamily: '"Roboto Slab",serif',
              color: colors.grey[800],
              fontWeight: 700,
              fontSize: 40,
              letterSpacing: 0.5,
              marginLeft: 0,
              padding: '40px 0 25px',
              [theme.breakpoints.down('md')]: {
                fontSize: 25,
              },
            },
            h2: {
              color: colors.grey[800],
              fontSize: 40,
              '&.maxLoanTitle': {
                fontSize: 20,
                marginBottom: '50px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: 16,
                  marginBottom: '20px',
                },
              },
              '&.submitMessage': {
                fontSize: 20,
                fontWeight: 700,
                marginBottom: '1em',
                [theme.breakpoints.down('sm')]: {
                  fontSize: 16,
                },
              },
              '&.largeRateDisplay': {
                fontWeight: 700,
                fontSize: 50,
                margin: '8px 0 16px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: 40,
                },
                '&.formDisplay': {
                  marginTop: 0,
                },
              },
            },
            h3: {
              fontFamily: '"Roboto Slab",serif',
              color: colors.grey[800],
              fontSize: 30,
              marginBottom: '24px',
              letterSpacing: '.2px',
              fontWeight: 700,
              [theme.breakpoints.down('lg')]: {
                fontSize: 22,
              },
              [theme.breakpoints.down('sm')]: {
                fontSize: 18,
                fontWeight: 700,
                marginBottom: '10px',
              },
              '&.mobileLarge': {
                [theme.breakpoints.down('lg')]: {
                  fontSize: 25,
                },
              },
            },
            h4: {
              color: colors.grey[800],
              fontSize: 35,
              fontWeight: 500,
              marginLeft: 0,
              '&.headerTitle': {
                fontSize    : 20,
                marginBottom: '.25rem',
              },
              '&.landingTitle': {
                fontWeight: 700,
                marginBottom: '20px',
                [theme.breakpoints.down('md')]: {
                  marginTop: '20px',
                },
                [theme.breakpoints.down('sm')]: {
                  fontSize: 25,
                },
              },
            },
            body2: {
              color: colors.grey[800],
              fontSize: 16,
              [theme.breakpoints.down('sm')]: {
                fontSize: 14,
              },
              '&.inline': {
                display: 'inline',
              },
              '&.small': {
                fontSize: 14,
                lineHeight: 1.3,
                '&.formTerms': {
                  color: colors.grey[800],
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 13,
                  },
                },
              },
              '&.splashBody': {
                [theme.breakpoints.down('md')]: {
                  fontSize: 16,
                  lineHeight: '21px',
                },
              },
              '&.arrayItem': {
                marginBottom: '3px',
              },
              '&.readOnlyValue': {
                padding: '4px 0 7px',
                lineHeight: '1.1875em',
                [theme.breakpoints.down('sm')]: {
                  paddingBottom: 0,
                },
              },
              '&.questionDisclaimer': {
                fontSize: 14,
                marginBottom: '1.25rem',
                color: colors.grey[500],
                display: 'inline-flex',
                alignItems: 'flex-start',
              },
            },
            body1: {
              color: colors.grey[800],
              fontSize: 16,
              [theme.breakpoints.down('sm')]: {
                fontSize: 14,
              },
              '&.condensedAlign': {
                [theme.breakpoints.down('md')]: {
                  lineHeight: '40px',
                },
              },
              '&.readOnlyLabel': {
                color: colors.grey[500],
                fontWeight: 500,
              },
              '&.assetTitle': {
                fontFamily: '"Roboto Slab",serif',
                fontWeight: 700,
                fontSize: 18,
                marginBottom: '16px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: 16,
                },
              },
              '&.submitError': {
                marginTop: '20px',
                [theme.breakpoints.down('sm')]: {
                  fontSize: 16,
                  marginTop: '-5px',
                  textAlign: 'center',
                },
              },
              '&.arrayTitle': {
                fontSize: 19,
                fontWeight: 500,
                [theme.breakpoints.down('sm')]: {
                  fontSize: 16,
                  lineHeight: '1.46429em',
                  marginBottom: '4px',
                },
              },
            },
            button: {
              textTransform: 'none',
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 400,
              '&:hover, &:focus': {
                textDecoration: 'underline',
              },
              [theme.breakpoints.down('sm')]: {
                fontSize: 14,
              },
            },
            // v5 compiles all Typography overrides into one class and emits media queries after plain declarations, so
            // the variant margins above (e.g. h6 at sm and up) would beat a plain gutterBottom margin. v4 kept rule order,
            // so gutterBottom won; putting it in a media query too restores that.
            gutterBottom: {
              [theme.breakpoints.up('xs')]: {
                marginBottom: '28px',
              },
              [theme.breakpoints.down('sm')]: {
                marginBottom: '20px',
              },
            },
            paragraph: {
              marginBottom: '22px',
              lineHeight: '23px',
              [theme.breakpoints.down('sm')]: {
                marginBottom: '16px',
              },
            },
          },
        },
        MuiButton: {
          // v5 changed the Button default color to 'primary'; v4's 'default' is closest to 'inherit'.
          defaultProps: {
            color: 'inherit',
          },
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontSize: 16,
              borderRadius: '3px',
              fontWeight: 400,
              '&.light': {
                fontWeight: 300,
              },
              '&.longButton': {
                width: '100%',
                minHeight: '50px',
                lineHeight: '22px',
                textAlign: 'center',
                padding: '5px 16px',
                margin: '2px 0 20px 0',
              },
              '&.roundedButton': {
                borderRadius: '20px',
                fontWeight  : 500,
                '&.MuiButton-sizeLarge': {
                  borderRadius: '25px',
                  minWidth    : '220px',
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                  },
                },
              },
              '&.smallTextButton': {
                fontSize  : 14,
                lineHeight: '1.46429em',
              },
              '&.leftHeaderButton': {
                minWidth: 0,
                paddingLeft: 0,
                [theme.breakpoints.down('sm')]: {
                  fontSize: 14,
                },
              },
              '&.rightHeaderButton': {
                minWidth: 0,
                paddingRight: 0,
                [theme.breakpoints.down('sm')]: {
                  fontSize: 14,
                },
              },
              '&.tabletButton': {
                fontSize: 18,
                minWidth: '250px',
                minHeight: '60px',
                height: '100%',
                padding: '12px 16px',
                '&.MuiButton-containedPrimary': {
                  '&.Mui-disabled': {
                    background: theme.palette.primary.main,
                    boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.12)',
                  },
                },
                [theme.breakpoints.down('md')]: {
                  fontSize: 20,
                },
                [theme.breakpoints.down('sm')]: {
                  fontWeight: 400,
                  fontSize: 16,
                  minWidth: '250px',
                  minHeight: '50px',
                  marginTop: 0,
                },
                // v4 set display: block on the Button label span here, which stacked the label and its
                // "(~1 minute)" note. v5 Buttons render children directly, so stack them on the button instead.
                '&.appButton': {
                  flexDirection: 'column',
                },
              },
              '&.link': {
                padding: 0,
                minWidth: '0',
                minHeight: '0',
                textDecoration: 'underline',
                justifyContent: 'unset',
                verticalAlign: 'unset',
                fontSize: 14,
                '&.termsLink': {
                  fontSize: 14,
                  display: 'inline',
                  verticalAlign: 'auto',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 13,
                  },
                },
                '&.semiBold': {
                  fontWeight: 500,
                },
                '&.navLink': {
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 16,
                  },
                },
                '&.viewAll': {
                  marginTop: '16px',
                },
                '&.continue': {
                  fontWeight: 700,
                  fontSize: 17,
                  [theme.breakpoints.down('sm')]: {
                    fontSize: 16,
                  },
                },
                '&.skip': {
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: '30px',
                },
                '&.noUnderline': {
                  textDecoration: 'none',
                },
                '&:hover': {
                  textDecoration: 'underline',
                  color: colors.common.black,
                },
              },
              '&.saveButton': {
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  minHeight: '50px',
                  marginTop: '10px',
                },
              },
              /* Full-width and stacked on mobile, side by side with a gap between on tablet/desktop -
               * for a row of several CTA buttons (see saveButton for the single-button case). */
              '&.stackedActionButton': {
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  marginBottom: '10px',
                },
                [theme.breakpoints.up('sm')]: {
                  marginRight: '15px',
                },
              },
              '&.mobileFixed': {
                minHeight: '50px',
                lineHeight: '50px',
                paddingTop: 0,
                paddingBottom: 0,
                fontWeight: 500,
                [theme.breakpoints.down('md')]: {
                  minHeight: '75px',
                  lineHeight: '75px',
                  borderRadius: 0,
                  fontSize: 18,
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  zIndex: 999,
                  width: '100%',
                },
              },
              '&.remove': {
                marginTop: '-16px',
                marginBottom: '32px',
                fontSize: 14,
                letterSpacing: '.16px',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
              '&.add': {
                [theme.breakpoints.up('sm')]: {
                  marginTop: '5px',
                },
              },
              '&.change': {
                margin: '30px 0',
                [theme.breakpoints.up('sm')]: {
                  margin: '40px 0',
                },
                '&.condensed': {
                  [theme.breakpoints.down('sm')]: {
                    marginBottom: '10px',
                  },
                },
              },
              '&.stackedButton': {
                marginBottom: '25px',
              },
            },
            containedPrimary: {
              fontWeight: 500,
            },
            // MuiSvgIcon '&.phone' sets 26px for the header icon. In v4 the Button start icon size (20px) outranked it;
            // v5 overrides outrank built-in styles, so restate the Button size for a phone start icon.
            startIcon: {
              '& > .MuiSvgIcon-root.phone': {
                fontSize: 20,
              },
            },
            text: {
              // v5 styleOverrides outrank MUI's built-in textPrimary/textSecondary colors (v4 overrides did not),
              // so only colorless text buttons get the grey.
              '&.MuiButton-textInherit': {
                color: colors.grey[800],
              },
              '&.accentColor': {
                color: colors.blue[700],
                '&:hover': {
                  color: darken(colors.blue[700], 0.1),
                },
              },
              '&:hover': {
                textDecoration: 'underline',
                background: 'none',
              },
            },
            fullWidth: {
              '&.tabletSizeLarge': {
                [theme.breakpoints.between('sm', 'md')]: {
                  maxWidth: '300px',
                  display: 'flex',
                  margin: '0 auto 25px',
                },
                [theme.breakpoints.down('sm')]: {
                  minHeight: '50px',
                },
              },
              '&.mobileOnly': {
                [theme.breakpoints.up('md')]: {
                  width: 'auto',
                },
              },
            },
            sizeSmall: {
              minWidth: '100px',
              minHeight: '40px',
              lineHeight: '40px',
              paddingTop: 0,
              paddingBottom: 0,
              fontSize: 16,
              '&.prefooterButton': {
                minWidth: '120px',
              },
              '&.mobileSmall': {
                [theme.breakpoints.down('sm')]: {
                  fontSize: 14,
                  minWidth: '90px',
                },
              },
              '&.brickButton': {
                borderRadius : '30px',
                minWidth     : '75px',
                minHeight    : '30px',
                lineHeight   : '30px',
                fontWeight   : 500,
                [theme.breakpoints.down('md')]: {
                  minHeight : '50px',
                  lineHeight: '50px',
                  maxWidth  : '220px',
                  width     : '100%',
                  margin: '5px 0',
                },
                [theme.breakpoints.down('sm')]: {
                  maxWidth: '100%',
                },
              },
              '&.roundedSmall': {
                [theme.breakpoints.up('md')]: {
                  minWidth  : '90px',
                  minHeight : '35px',
                  lineHeight: '35px',
                },
                '&.mobileFullWidth': {
                  [theme.breakpoints.down('sm')]: {
                    width       : '100%',
                    minHeight   : '50px',
                    lineHeight  : '50px',
                    borderRadius: '25px',
                    margin      : '10px 0',
                  },
                },
              },
              '&.buttonSpacing': {
                margin: '0 12px',
              },
            },
            sizeLarge: {
              minWidth: '100%',
              minHeight: '50px',
              lineHeight: '50px',
              fontSize: 16,
              padding: '0px 16px',
              [theme.breakpoints.up('sm')]: {
                minWidth: '200px',
              },
              '&.mobileContained': {
                minWidth: '190px',
              },
              '&.xLarge': {
                [theme.breakpoints.up('sm')]: {
                  minWidth: '250px',
                  minHeight: '60px',
                  lineHeight: '60px',
                },
              },
            },
            contained: {
              '&.Mui-disabled': {
                color: colors.common.white,
                backgroundColor: colors.grey[400],
              },
              '&.tertiary': {
                background: theme.palette.tertiary.main,
                color: theme.palette.tertiary.contrastText,
                '&:hover': {
                  background: theme.palette.tertiary.dark,
                },
                '&.Mui-disabled': {
                  background: colors.grey[400],
                },
              },
              '&.accentColor': {
                background: colors.blue[700],
                color: colors.common.white,
                '&:hover': {
                  background: darken(colors.blue[700], 0.1),
                },
                '&.Mui-disabled': {
                  background: colors.grey[400],
                },
              },
              '&.white': {
                background: colors.common.white,
                '&.accentColorText': {
                  color: colors.blue[700],
                },
                '&:hover': {
                  background: darken(colors.common.white, 0.1),
                },
              },
            },
            outlined: {
              color: colors.grey[800],
              borderColor: colors.grey[800],
              '&:focus, &:hover': {
                textDecoration: 'none',
              },
              '&.Mui-disabled': {
                color: colors.grey[400],
                borderColor: colors.grey[400],
              },
              '&.tertiary': {
                borderColor: theme.palette.tertiary.main,
                color: theme.palette.tertiary.main,
                '&:hover': {
                  color: theme.palette.tertiary.dark,
                  borderColor: theme.palette.tertiary.dark,
                },
              },
              '&.white': {
                borderColor: colors.common.white,
                color: colors.common.white,
                '&:hover': {
                  background: darken(colors.common.white, 0.1),
                },
                '&.accentColor': {
                  '&:hover': {
                    color: colors.blue[700],
                  },
                },
              },
            },
            outlinedPrimary: {
              borderColor: theme.palette.primary.main,
              color: colors.grey[800],
              '&:focus, &:hover': {
                background: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              },
              // MUI's own (hover: none) reset for touch devices sets the hover background back to
              // transparent (emitted after the rule above), leaving that white text on a white page.
              // The doubled class outranks the reset regardless of order.
              '@media (hover: none)': {
                '&&:focus, &&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              },
            },
          },
        },
        MuiLink: {
          styleOverrides: {
            root: {
              color: colors.grey[900],
              '&:hover': {
                color: theme.palette.primary.main,
              },
            },
          },
        },
        MuiInput: {
          styleOverrides: {
            root: {
              '&.outlined': {
                border      : `1px solid ${colors.grey[300]}`,
                borderRadius: '2px',
                alignItems  : 'center',
              },
              '&.light': {
                fontWeight: 300,
              },
            },
            input: {
              '&.percentField': {
                width: '100%',
                marginTop: 16,
              },
              '&.editableInput': {
                marginTop: '10px',
                fontWeight: 500,
              },
              '&.indent': {
                padding: '9px',
              },
            },
            underline: {
              '&:after': {
                borderBottom: `2px solid ${theme.palette.primary.main}`,
              },
              // v4 drew the 2px error underline whether or not the field was focused; v5 only shows it on focus.
              '&.Mui-error:after': {
                transform: 'scaleX(1)',
              },
            },
          },
        },
        // v5 raised the input line height from 1.1876em (19px) to 1.4375em (23px). Single-line inputs keep their v5
        // height, which already matches v4 (23px + 9px padding vs 19px + 13px), so only the line height changes; multi-line
        // inputs grow by line height per row, so they also get the v4 padding back.
        MuiInputBase: {
          styleOverrides: {
            root: {
              lineHeight: '1.1876em',
            },
            multiline: {
              padding: '6px 0 7px',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            multiline: {
              padding: '18.5px 14px',
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            // v5 colors adornments with action.active (54% black); v4 inherited the field text color.
            root: {
              color: 'inherit',
            },
            positionEnd: {
              '&.percent': {
                marginLeft: 2,
                marginTop: 10,
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              '&.buttonClose': {
                width: '40px',
                height: '40px',
                position: 'absolute',
                right: '-22px',
                top: '-8px',
                [theme.breakpoints.down('sm')]: {
                  right: '-12px',
                },
              },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              color: colors.grey[800],
              // v5 changed the FormLabel line height from 1 to 1.4375em, which makes legends and labels taller.
              lineHeight: 1,
              '&.legend': {
                color: colors.grey[800],
                fontWeight: 700,
                fontSize: 18,
                letterSpacing: '.2px',
                paddingTop: '20px',
                [theme.breakpoints.down('md')]: {
                  fontSize: 16,
                },
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            // v5 offsets standard labels by 20px to suit its 1.4375em line height; with the v4 line height of 1
            // (see MuiFormLabel) the v4 offset of 24px lines the label up with the input again.
            standard: {
              '&.MuiInputLabel-formControl:not(.MuiInputLabel-shrink)': {
                transform: 'translate(0, 24px) scale(1)',
              },
            },
            // Outlined labels: v4 offsets to go with the v4 line height. v4's own outlined shrink position outranked the
            // shrink override below; v5 overrides outrank built-in styles, so restate it here.
            outlined: {
              '&.MuiInputLabel-formControl:not(.MuiInputLabel-shrink)': {
                transform: 'translate(14px, 20px) scale(1)',
              },
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -6px) scale(0.75)',
              },
            },
            shrink: {
              transform: 'translate(0, 1.5px) scale(0.85)',
              fontWeight: 500,
              color: colors.grey[800],
              letterSpacing: '.2px',
            },
          },
        },
        MuiStepper: {
          styleOverrides: {
            root: {
              background: 'none',
              padding: '10px 0 25px',
              [theme.breakpoints.down('sm')]: {
                paddingBottom: '16px',
              },
            },
          },
        },
        MuiStepConnector: {
          styleOverrides: {
            root: {
              display: 'none',
            },
          },
        },
        MuiStep: {
          styleOverrides: {
            horizontal: {
              width: '100%',
              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
        },
        MuiStepButton: {
          styleOverrides: {
            root: {
              display: 'block',
              margin: '-24px 0',
              padding: '24px 0',
              [theme.breakpoints.down('sm')]: {
                margin: '-12px 0',
                padding: '12px 0',
              },
            },
          },
        },
        MuiStepLabel: {
          styleOverrides: {
            root: {
              border: `2.5px solid ${theme.palette.primary.main}`,
              // v5 styleOverrides do not support state keys such as `disabled`; target the state class instead.
              '&.Mui-disabled': {
                borderColor: colors.grey[300],
              },
            },
          },
        },
        MuiNativeSelect: {
          styleOverrides: {
            icon: {
              color: colors.grey[800],
              marginRight: '-3px',
            },
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              color: theme.palette.primary.main,
            },
          },
        },
        MuiFormControl: {
          // v5 changed the FormControl default variant to 'outlined'; keep v4's 'standard'.
          defaultProps: {
            variant: 'standard',
          },
          styleOverrides: {
            root: {
              marginBottom: '26px',
              '&.editableField': {
                marginBottom: '20px',
              },
              '&.noMargin': {
                marginBottom: 0,
              },
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              '&.isolatedToggle': {
                border: 0,
                padding: 0,
              },
            },
            label: {
              '&.largeToggle': {
                [theme.breakpoints.up('sm')]: {
                  fontSize: 20,
                },
              },
            },
          },
        },
        MuiSvgIcon: {
          styleOverrides: {
            root: {
              '&.alignDefaultSize': {
                float: 'left',
                marginRight: '5px',
              },
              '&.alignChevronLeft': {
                marginLeft: '-6px',
              },
              '&.phone': {
                transform: 'rotate(270deg)',
                fontSize: 26,
                marginLeft: '5px',
              },
              '&.search': {
                color: colors.grey[800],
              },
              '&.warning': {
                fontSize: 18,
                marginLeft: '8px',
                verticalAlign: 'text-top',
                [theme.breakpoints.down('sm')]: {
                  marginLeft: '5px',
                  verticalAlign: 'sub',
                },
                '&.left': {
                  marginLeft: 0,
                  marginRight: '8px',
                  [theme.breakpoints.down('sm')]: {
                    marginLeft: '5px',
                  },
                },
              },
              '&.info': {
                fontSize: 18,
                marginRight: '5px',
                verticalAlign: 'top',
              },
              '&.buttonCheck': {
                marginLeft: '2px',
                marginTop: '-.125em',
              },
              '&.closeModal': {
                [theme.breakpoints.down('sm')]: {
                  fontSize: 22,
                },
              },
              '&.start': {
                marginRight: '4px',
              },
              '&.mobileFullWidthArrow': {
                [theme.breakpoints.down('md')]: {
                  fontSize: 38,
                  color: colors.grey[800],
                  position: 'absolute',
                  right: 0,
                },
              },
              '&.account': {
                fontSize: 36,
                color: colors.grey[800],
              },
              '&.marginLeft': {
                marginLeft: '10px',
              },
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            colorPrimary: {
              height: 'auto',
              '&.Mui-checked': {
                color: theme.palette.primary.dark,
              },
            },
          },
        },
        MuiGrid: {
          styleOverrides: {
            container: {
              '&.headerWrapper': {
                paddingTop: '70px',
                [theme.breakpoints.down('sm')]: {
                  paddingTop: '46px',
                },
              },
              '&.headerContainer': {
                maxWidth: '900px',
                width: '100%',
                margin: '0 auto',
                padding: '15px 0',
                [theme.breakpoints.down('sm')]: {
                  padding: 0,
                },
              },
              '&.footerItemContainer': {
                padding: '10px 0px',
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                  alignItems   : 'center',
                },
              },
              '&.footerTextContainer': {
                padding: theme.spacing(2, 0),
              },
              '&.logo': {
                height: '40px',
                margin: '0 auto',
                textAlign: 'center',
                display: 'block',
                maxWidth: '100%',
                [theme.breakpoints.down('md')]: {
                  height: '30px',
                },
              },
              '&.tabletSpacing': {
                [theme.breakpoints.between('sm', 'md')]: {
                  padding: '0 26px',
                },
              },
              '&.verifyContainer': {
                borderTop: `1px solid ${colors.grey[300]}`,
                paddingTop: '10px',
              },
              '&.shadeCover': {
                '&.mobileOnly': {
                  [theme.breakpoints.down('md')]: {
                    background: 'rgba(0,0,0,.6)',
                    height    : '100%',
                    width     : '100%',
                    paddingTop: '100px',
                  },
                },
              },
              '&.canvas': {
                backgroundColor: colors.grey[100],
                minHeight      : '500px',
                paddingTop     : '22px',
                paddingBottom  : '50px',
                [theme.breakpoints.up('xl')]: {
                  minHeight: '700px',
                },
                [theme.breakpoints.down('md')]: {
                  minHeight      : '100%',
                  height         : '100%',
                  paddingTop     : 0,
                  paddingBottom  : 0,
                  backgroundColor: colors.common.white,
                },
                '&.withoutForm': {
                  paddingTop: '62px',
                  [theme.breakpoints.down('md')]: {
                    paddingTop: '20px',
                  },
                  '&.mobileSplash': {
                    [theme.breakpoints.down('md')]: {
                      paddingTop: '0',
                      background: 'none',
                    },
                  },
                },
              },
              '&.detailContainer': {
                [theme.breakpoints.up('md')]: {
                  padding: '15px 0',
                },
              },
              '&.banner': {
                padding: '35px 16px',
                textAlign: 'center',
              },
              '&.lightBackground': {
                background: colors.grey[100],
              },
              '&.mediumBackground': {
                background: colors.grey[200],
                '&.banner': {
                  [theme.breakpoints.down('md')]: {
                    padding: '25px 16px',
                  },
                },
              },
              '&.whiteMobileBackground': {
                [theme.breakpoints.down('md')]: {
                  background: colors.common.white,
                },
              },
              '&.titleContainer': {
                marginBottom: '40px',
                [theme.breakpoints.down('sm')]: {
                  marginBottom: '15px',
                },
              },
              '&.modalContainer': {
                top: `50%`,
                left: `50%`,
                transform: `translate(-50%, -50%)`,
                display: 'block',
                position: 'absolute',
                maxWidth: '800px',
                width: '100%',
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[5],
                padding: '12px 26px 26px',
                maxHeight: '80%',
                '&:focus': {
                  outline: 0,
                },
                [theme.breakpoints.down('md')]: {
                  maxWidth : '500px',
                  overflowY: 'scroll',
                },
                [theme.breakpoints.down('sm')]: {
                  maxWidth: '300px',
                  padding: '12px 16px 16px',
                },
              },
              '&.closeWrapper': {
                paddingBottom: '24px',
                display: 'block',
              },
              '&.formTermsWrapper': {
                padding: '10px 0',
                justifyContent: 'flex-end',
                [theme.breakpoints.up('md')]: {
                  height: '40px',
                },
                [theme.breakpoints.down('sm')]: {
                  justifyContent: 'center',
                  paddingBottom: 0,
                },
              },
              '&.mobileCenter': {
                [theme.breakpoints.down('sm')]: {
                  justifyContent: 'center',
                },
              },
              '&.assetTotal': {
                [theme.breakpoints.up('sm')]: {
                  padding: '10px 0',
                },
              },
              '&.centerLargeItem': {
                maxWidth: '250px',
                width: '100%',
                margin: '0 auto',
                [theme.breakpoints.up('md')]: {
                  marginBottom: '20px',
                },
                [theme.breakpoints.down('md')]: {
                  maxWidth: '100%',
                },
                '&.field': {
                  display: 'block',
                },
              },
              '&.mediumContainer': {
                maxWidth: '975px',
                width: '100%',
                margin: '0 auto',
              },
              '&.prefooterWrapper': {
                backgroundColor: colors.grey[100],
                padding: '2.5rem 1rem',
                marginTop: '4rem',
                borderBottom: `6px solid ${theme.palette.secondary.main}`,
                [theme.breakpoints.down('md')]: {
                  padding: '1.75rem 1rem',
                  marginTop: 0,
                },
              },
              '&.heroImage': {
                height: '250px',
                marginBottom: '16px',
              },
              '&.branchHero': {
                height: '300px',
                padding: '0 16px',
                [theme.breakpoints.down('sm')]: {
                  height: '200px',
                },
              },
              '&.fullTextArea': {
                width: '100%',
                '& > .wrapper': {
                  width: '100%',
                  maxWidth: '100%',
                },
              },
              '&.formNav': {
                marginTop: '40px',
                [theme.breakpoints.down('sm')]: {
                  marginTop: '30px',
                },
              },
              '&.indent': {
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                paddingLeft: '12px',
                [theme.breakpoints.down('md')]: {
                  margin: '10px 0',
                  borderLeft: 'none',
                  paddingLeft: 0,
                },
              },
              '&.section': {
                borderBottom: `1px solid ${colors.grey[300]}`,
                marginBottom: '12px',
                paddingBottom: '10px',
              },
              '&.branchContainer': {
                maxWidth: '900px',
                width: '100%',
                margin: '0 auto',
                padding: '16px 0',
                textAlign: 'center',
                [theme.breakpoints.down('md')]: {
                  maxWidth: '100%',
                  padding: '0 24px',
                },
                [theme.breakpoints.down('sm')]: {
                  padding: '0 16px',
                },
                '&.mobileFullWidth': {
                  padding: 0,
                  [theme.breakpoints.down('lg')]: {
                    maxWidth: '100%',
                  },
                },
                '&.branchContent': {
                  [theme.breakpoints.up('md')]: {
                    minHeight: '400px',
                  },
                },
              },
              '&.branchButtonContainer': {
                maxWidth: '580px',
                width: '100%',
                margin: '50px auto 0',
                [theme.breakpoints.down('md')]: {
                  flexDirection: 'column',
                  marginTop: '20px',
                },
                [theme.breakpoints.down('sm')]: {
                  marginTop: '30px',
                },
                '&.numberOfButtons-3': {
                  maxWidth: '880px',
                },
                '&.numberOfButtons-4': {
                  [theme.breakpoints.between('sm', 'md')]: {
                    flexDirection: 'row',
                  },
                },
              },
              '&.formWrapper': {
                display: 'block',
                maxWidth: '700px',
                width: '100%',
                margin: '0 auto',
                padding: '16px 0',
                [theme.breakpoints.down('md')]: {
                  maxWidth: '100%',
                  padding: '0 24px',
                },
                [theme.breakpoints.down('sm')]: {
                  padding: '0 16px',
                },
                '&.topSectionPadding': {
                  padding: '22px 16px',
                  [theme.breakpoints.up('md')]: {
                    marginTop: '10px',
                  },
                },
                '&.schedule': {
                  paddingTop: '40px',
                  paddingBottom: '40px',
                  [theme.breakpoints.down('md')]: {
                    maxWidth: '700px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    paddingTop: '25px',
                    paddingBottom: '25px',
                  },
                },
                '&.stepper': {
                  [theme.breakpoints.up('md')]: {
                    padding: 0,
                  },
                  [theme.breakpoints.down('md')]: {
                    paddingTop: '22px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    paddingTop: '12px',
                  },
                  '&.summary': {
                    paddingBottom: 0,
                  },
                },
              },
              '&.summaryContainer': {
                [theme.breakpoints.down('md')]: {
                  padding: '0 24px 22px',
                },
                [theme.breakpoints.down('sm')]: {
                  padding: '0 16px 22px',
                },
              },
              '&.recalculateContainer': {
                marginBottom: '28px',
                [theme.breakpoints.up('md')]: {
                  marginTop: '30px',
                },
              },
              '&.sectionHeader': {
                marginBottom: '16px',
                padding: '22px 26px 30px',
                [theme.breakpoints.up('md')]: {
                  borderTop: `1px solid ${theme.palette.primary.main}`,
                },
                [theme.breakpoints.down('md')]: {
                  background: colors.grey[100],
                },
                [theme.breakpoints.down('sm')]: {
                  padding: '22px 16px',
                },
              },
              '&.recalculateTitleOnly': {
                padding: '30px 26px 0',
                [theme.breakpoints.down('sm')]: {
                  padding: '22px 16px 16px',
                },
              },
              '&.wideGrid': {
                padding: theme.spacing(2),
                maxWidth: '1212px',
                width: '100%',
                margin: '0 auto',
                [theme.breakpoints.between('sm', 'md')]: {
                  paddingLeft: '24px',
                  paddingRight: '24px',
                },
                // Keep removeMobilePadding before sectionSpacing: v4 (JSS) emitted sectionSpacing's mobile padding last,
                // so a container with both classes kept its 40px bottom padding on mobile (broker About Us section).
                '&.removeMobilePadding': {
                  [theme.breakpoints.down('md')]: {
                    padding: 0,
                  },
                },
                '&.sectionSpacing': {
                  paddingBottom: '60px',
                  paddingTop: 0,
                  [theme.breakpoints.down('md')]: {
                    paddingBottom: '40px',
                  },
                },
                '&.disclaimerContainer': {
                  paddingTop: '26px',
                  paddingBottom: '10px',
                  [theme.breakpoints.down('md')]: {
                    padding: '16px 26px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    padding: '8px 16px 16px',
                  },
                },
              },
            },
            item: {
              '&.lightBackground': {
                background: colors.grey[100],
              },
              '&.gridButton': {
                marginBottom: '30px',
                [theme.breakpoints.down('sm')]: {
                  marginBottom: '20px',
                },
              },
              '&.centerToggle': {
                maxWidth: '400px',
                margin: '0 auto',
              },
              '&.termItem': {
                [theme.breakpoints.up('md')]: {
                  position: 'absolute',
                  bottom: '72px',
                  right: 0,
                },
              },
              '&.sectionFooterItem': {
                [theme.breakpoints.up('md')]: {
                  textAlign: 'center',
                },
                [theme.breakpoints.down('md')]: {
                  borderTop: `1px solid ${colors.grey[300]}`,
                },
              },
              '&.closeContainer': {
                position: 'relative',
                textAlign: 'right',
              },
              '&.wrapper': {
                position: 'relative',
                width: '100%',
                [theme.breakpoints.up('md')]: {
                  maxWidth: '350px',
                },
              },
              '&.textRight': {
                textAlign: 'right',
              },
              '&.textCenter': {
                textAlign: 'center',
              },
              '&.textLeft': {
                textAlign: 'left',
              },
              '&.schedule': {
                margin: '10px 0 20px',
                [theme.breakpoints.down('md')]: {
                  margin: '10px 0 30px',
                },
              },
              '&.splitWrapper': {
                [theme.breakpoints.up('md')]: {
                  padding: '4px 26px 16px',
                },
              },
              '&.splitImage': {
                minHeight: '500px',
                display: 'block',
              },
              '&.noStepper': {
                [theme.breakpoints.up('md')]: {
                  marginTop: '20px',
                },
              },
              '&.noStepQuestion': {
                paddingTop: '20px',
              },
              '&.heroContent': {
                width: '100%',
              },
              '&.formNavItem': {
                [theme.breakpoints.up('sm')]: {
                  marginRight: '42px',
                },
                [theme.breakpoints.down('sm')]: {
                  width: '100%',
                  textAlign: 'center',
                  marginBottom: '16px',
                },
              },
              '&.assetIndent': {
                paddingBottom: '20px',
                [theme.breakpoints.up('md')]: {
                  marginLeft: '16px',
                  paddingBottom: '30px',
                },
              },
              '&.borderBox': {
                [theme.breakpoints.down('md')]: {
                  border: `1px solid ${colors.grey[300]}`,
                },
              },
            },
          },
        },
        MuiFormGroup: {
          styleOverrides: {
            root: {
              '&.topLevel': {
                [theme.breakpoints.down('md')]: {
                  padding: '5px 0 10px',
                  borderTop: `1px solid ${colors.grey[300]}`,
                },
                '&.first': {
                  borderTop: 'none',
                },
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              '&.formWrapper': {
                display: 'block',
                maxWidth: '700px',
                width: '100%',
                margin: '0 auto',
                padding: '16px 26px',
                position: 'relative',
                [theme.breakpoints.down('md')]: {
                  maxWidth: '100%',
                  padding: '0 24px',
                  minHeight: 0,
                  boxShadow: 'none',
                },
                [theme.breakpoints.down('sm')]: {
                  padding: '0 16px',
                },
                '&.paperWithoutForm': {
                  [theme.breakpoints.up('md')]: {
                    marginTop: '10px',
                  },
                },
                '&.topSectionPaper': {
                  marginBottom: '30px',
                  [theme.breakpoints.down('md')]: {
                    background: colors.grey[50],
                    padding: '16px',
                    boxShadow: '0 2px 4px rgba(181,181,181,0.5)',
                    marginBottom: '4px',
                  },
                },
                '&.sectionFooter': {
                  marginTop: '30px',
                  [theme.breakpoints.down('md')]: {
                    padding: 0,
                    borderTop: `5px solid ${colors.grey[300]}`,
                    borderBottom: `5px solid ${theme.palette.secondary.main}`,
                  },
                  '&.removeSpacing': {
                    [theme.breakpoints.down('md')]: {
                      marginTop: 0,
                    },
                  },
                },
                '&.fieldWrapper': {
                  [theme.breakpoints.up('md')]: {
                    padding: 0,
                    boxShadow: 'none',
                  },
                  [theme.breakpoints.down('md')]: {
                    marginTop: '50px',
                    paddingTop: '16px',
                    paddingBottom: '30px',
                  },
                },
                '&.topPaper': {
                  paddingTop: '60px',
                },
              },
              '&.mobileTransparent': {
                [theme.breakpoints.down('md')]: {
                  background: 'none',
                },
              },
            },
            elevation2: {
              '&.stopGate': {
                [theme.breakpoints.up('md')]: {
                  padding: 0,
                },
              },
              '&.noPadding': {
                padding: 0,
              },
              '&.clearWrapper': {
                boxShadow: 'none',
                [theme.breakpoints.up('md')]: {
                  background: colors.grey[100],
                  maxWidth: '752px',
                  paddingTop: 0,
                },
              },
              '&.mobileHideShadow': {
                [theme.breakpoints.down('md')]: {
                  boxShadow: 'none',
                },
              },
            },
          },
        },
        // MuiExpansionPanel* overrides were dropped in the v5 spike: they never applied in v4.12 either,
        // because Accordion reads the MuiAccordion* theme keys.
        MuiAppBar: {
          styleOverrides: {
            root: {
              '&.header': {
                boxShadow: '0 2px 6px 0 rgba(229,229,229,0.5)',
                [theme.breakpoints.down('md')]: {
                  boxShadow   : 'none',
                  borderBottom: `1px solid ${colors.grey[300]}`,
                },
              },
            },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {},
            regular: {
              '&.headerHeight': {
                height: '70px',
                minHeight: 0,
                [theme.breakpoints.down('sm')]: {
                  height: '46px',
                },
              },
            },
          },
        },
        MuiSelect: {
          // v5 changed the Select default variant to 'outlined'; keep v4's 'standard'.
          defaultProps: {
            variant: 'standard',
          },
          styleOverrides: {
            root: {
              color: colors.grey[800],
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              color: colors.grey[800],
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            html: {
              fontFamily: theme.typography.fontFamily,
            },
            // v5 CssBaseline uses body1 for body text; v4 used body2.
            body: {
              ...theme.typography.body2,
            },
            a: {
              textDecoration: 'none',
              '&:focus': {
                outline: 0,
              },
            },
          },
        },
        // v5 derives standard Alert colors from palette[severity].light; v4 (lab) used .main, which gave darker text
        // and a slightly different background. Restore the v4 formula for each severity.
        MuiAlert: {
          styleOverrides: {
            ...Object.fromEntries((['success', 'info', 'warning', 'error'] as const).map((severity) => [
              `standard${severity.charAt(0).toUpperCase()}${severity.slice(1)}`,
              {
                color: darken(theme.palette[severity].main, 0.6),
                backgroundColor: lighten(theme.palette[severity].main, 0.9),
                '& .MuiAlert-icon': {
                  color: theme.palette[severity].main,
                },
              },
            ])),
          },
        },
        // v5 changed the TextField default variant to 'outlined'; keep v4's 'standard'.
        MuiTextField: {
          defaultProps: {
            variant: 'standard',
          },
        },
      },
    };

    return (
      <CacheProvider value={getEmotionCache()}>
        <ThemeProvider theme={theme}>
          {this.props.children}
        </ThemeProvider>
      </CacheProvider>
    );
  }
}

const mapStateToProps = (state: any) => {
  return ({
    primaryColor  : state.config.primaryColor,
    secondaryColor: state.config.secondaryColor,
    tertiaryColor : state.config.tertiaryColor,
  });
};

export const ThemeWrapper = connect(mapStateToProps)(ThemeWrapperComponent);
